<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title v-if="!wizardMode" class="card-header text-left fixed-card-title2">
			<v-row>
				<v-col cols="10">
					<v-toolbar-title>{{ $t('assign-staff') }}</v-toolbar-title>
				</v-col>
				<v-col cols="2" class="text-right d-flex align-center justify-end">
					<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
				</v-col>
			</v-row>
		</v-card-title>
		<v-card-text
			class="pt-4 overflow-y-auto"
			:class="{ 'pb-2': wizardMode }"
			:style="{ height: wizardMode ? 'calc(95vh - 122px)' : 'calc(100vh - 62px)' }">
			<v-row dense>
				<v-col cols="6">
					<v-text-field
						hide-details
						dense
						v-model="searchHospital"
						:label="$t('search') + ' ' + $t('facility')"
						outlined
						append-icon="mdi-magnify" />
				</v-col>
				<v-col cols="6">
					<v-text-field
						hide-details
						dense
						v-model="searchStaff"
						:label="$t('search') + ' ' + $t('staff')"
						outlined
						append-icon="mdi-magnify" />
				</v-col>
				<v-col cols="6">
					<v-radio-group v-model="selectedHospitalId" hide-details class="mt-0">
						<v-data-table
							outlined
							dense
							:items="filteredHospitals"
							:no-data-text="$t('no-data-available')"
							:loading-text="$t('loading')"
							fixed-header
							:search="searchHospital"
							:height="wizardMode ? 'calc(95vh - 259.2px)' : pageHeight(143)"
							:headers="facilityHeader"
							:loading="loading"
							class="data-table-outline"
							:items-per-page="-1">
							<template #[`item.hospitalName`]="{ item }">
								<div class="d-flex align-center">
									<v-icon color="primary" class="mr-2">mdi-hospital-building</v-icon>
									{{ item.hospitalName }}
								</div>
							</template>
							<template #[`item.action`]="{ item }">
								<div class="d-flex align-center justify-end">
									<v-radio color="primary" :value="item.id" class="ma-0 pa-0" hide-details />
								</div>
							</template>
						</v-data-table>
					</v-radio-group>
				</v-col>
				<v-col cols="6">
					<v-data-table
						outlined
						dense
						v-model="selectedMedicalStaff"
						:items="staff"
						:no-data-text="$t('no-data-available')"
						:loading-text="$t('loading')"
						fixed-header
						item-key="id"
						:search="searchStaff"
						:height="wizardMode ? 'calc(95vh - 259.2px)' : pageHeight(143)"
						:headers="staffHeader"
						:loading="loading"
						class="data-table-outline"
						checkbox-color="primary"
						show-select
						:items-per-page="-1">
						<template #[`header.data-table-select`]="{ props }">
							<v-simple-checkbox v-bind="props" @click="onSelectAllClick" />
						</template>
						<template #item="{ item }">
							<tr>
								<td>
									<div v-if="item.isMemberOfJustOneHospital && item.isAssignToHospital">
										<v-tooltip color="primary" top>
											<template v-slot:activator="{ on }">
												<v-simple-checkbox
													v-on="on"
													v-model="item.isAssignToHospital"
													:disabled="item.isAssignToHospital"
													@click="!item.isAssignToHospital && onSelectionChange(item)"
													color="primary"
													class="ma-0 pa-0" />
											</template>
											<span>{{ $t('user-is-member-of-just-one-hospital') }}</span>
										</v-tooltip>
									</div>
									<v-simple-checkbox
										v-else
										v-model="item.isAssignToHospital"
										@click="onSelectionChange(item)"
										color="primary"
										class="ma-0 pa-0" />
								</td>
								<td class="text-left">{{ item.fullName }}</td>
							</tr>
						</template>
					</v-data-table>
				</v-col>
			</v-row>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
	name: 'DashboardCoreAppBar',
	components: {},
	props: {
		wizardMode: {
			type: Boolean,
			default: false,
		},
		wizardData: {
			type: Object,
			default: () => ({}),
		},
	},
	data: () => ({
		selectedHospitalId: 0,
		selectedMedicalStaff: [],
		staff: [],
		searchStaff: '',
		searchHospital: '',
		loading: false,
	}),
	computed: {
		...mapState({
			hospitals: (state) => state.hospitals.hospitals,
			medicalTeams: (state) => state.medicalteam.medicalTeam,
			userData: (state) => state.authentication.userData,
			relatedMedicalStaff: (state) => state.hospitals.users,
			medicalStaffAssignedHospitals: (state) => state.hospitals.medicalStaffAssignedHospitals,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		staffHeader () {
			return [
				{
					text: this.$t('name'),
					align: 'left',
					class: 'table-header px-4',
					value: 'fullName',
				},
			];
		},
		facilityHeader () {
			return [
				{ text: this.$t('name'), align: 'left', value: 'hospitalName' },
				{ text: '', align: 'end', value: 'action' },
			];
		},
		filteredHospitals () {
			if (this.wizardMode) {
				this.selectedHospitalId = this.wizardData.id;
				return [this.wizardData];
			}
			if (this.hospitals == null) {
				return [];
			}
			return this.hospitals;
		},
	},
	async mounted () {
		this.$store.commit('SET_ASSIGN_DRAWER_WIDTH', 960);
		await this.getAllHospitals();
	},
	methods: {
		async onSelectAllClick () {
			const body = {
				hospitalId: this.selectedHospitalId,
				userId: this.staff.map((m) => m.id),
				organizationId: this.userData.organizationId,
			};
			this.loading = true;

			if (this.selectedMedicalStaff.length === this.staff.length) {
				await this.$store.dispatch('hospitals/deassignMultipleMedicalStaffFromHospitalv2', body);
			} else {
				await this.$store.dispatch('hospitals/assignMultipleUsersToHospital', body);
			}
			await this.getRelatedMedicalStaff();
			this.loading = false;
		},
		async onSelectionChange (item) {
			if (!item.isAssignToHospital) {
				this.deassignUserFromHospital({ id: item.id });
			} else {
				this.assignUserToHospital({ id: item.id });
			}
		},
		async assignUserToHospital (item) {
			const body = {
				hospitalId: this.selectedHospitalId,
				userId: [item.id],
				organizationId: this.userData.organizationId,
			};
			this.loading = true;
			await this.$store.dispatch('hospitals/assignMultipleUsersToHospital', body);
			await this.getRelatedMedicalStaff();
			this.loading = false;
		},
		async deassignUserFromHospital (item) {
			const body = {
				hospitalId: this.selectedHospitalId,
				userId: [item.id],
				organizationId: this.userData.organizationId,
			};
			this.loading = true;
			await this.$store.dispatch('hospitals/deassignMultipleMedicalStaffFromHospitalv2', body);
			await this.getRelatedMedicalStaff();
			this.loading = false;
		},
		async getAllHospitals () {
			if (this.hospitals == null) {
				this.loading = true;
				await this.$store.dispatch('hospitals/getHospitals');
				this.loading = false;
			}
			if (this.hospitals?.length && !this.selectedHospitalId) {
				this.selectedHospitalId = this.hospitals[0].id;
			}
		},
		closeDrawer () {
			this.selectedHospitalId = 0;
			this.selectedMedicalStaff = [];
			this.staff = [];
			this.$store.commit('SET_ASSIGN_DRAWER_WIDTH');
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		async getRelatedMedicalStaff () {
			try {
				this.loading = true;
				await this.$store.dispatch('hospitals/getRelatedMedicalStaffForHospitalV3', this.selectedHospitalId);
				this.staff = this.medicalStaffAssignedHospitals
					.filter((user) => user.roleId === 3 || user.roleId === 4)
					.map((user) => ({
						id: user.userId,
						fullName: user.userName,
						role: user.roleName,
						roleId: user.roleId,
						isAssignToHospital: user.isAssignToHospital,
						isMemberOfJustOneHospital: user.isMemberOfJustOneHospital,
					}));
				this.selectedMedicalStaff = this.staff.filter((f) => f.isAssignToHospital);
			} catch (error) {
				this.staff = [];
			} finally {
				this.loading = false;
			}
		},
	},
	watch: {
		selectedHospitalId (newVal) {
			if (newVal > 0) {
				this.getRelatedMedicalStaff();
			}
			if (this.wizardMode) {
				this.$emit('update:selectedMedicalStaff', newVal);
			}
		},
		selectedMedicalStaff (newVal) {
			if (this.wizardMode) {
				this.$emit('update:selectedMedicalStaff', newVal);
			}
		},
	},
};
</script>
